import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db, uploadFile, BUCKETS } from '../lib/supabase';
import { Card, Avatar, Btn, Input, C, Badge } from '../components/UI';
import { MFASetup } from '../components/MFAGate';
import { isNotifSoundEnabled, setNotifSoundEnabled } from '../hooks/useNotifications';

// ── Username validation ───────────────────────────────────────────────────────
function validateUsername(u) {
  if (!u) return 'Username is required.';
  if (u.length < 5) return 'Must be at least 5 characters.';
  if (u.length > 30) return 'Max 30 characters.';
  if (!/^[a-z0-9._-]+$/.test(u)) return 'Only lowercase letters, numbers, . _ - allowed.';
  if (/^[._-]/.test(u) || /[._-]$/.test(u)) return 'Cannot start or end with . _ -';
  return null;
}

function generateUsername(name, email) {
  const base = (name || email || 'user')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '.')
    .replace(/\.+/g, '.')
    .replace(/^\.|\.$/, '')
    .slice(0, 15) || 'user';
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}${suffix}`;
}

export default function ProfilePage({ data, onToast }) {
  const { user, profile, refreshProfile } = useAuth();
  const [form, setForm] = useState({
    full_name:  profile?.full_name  || '',
    timezone:   profile?.timezone   || 'Asia/Kolkata',
    notifications_enabled: profile?.notifications_enabled ?? true,
    digest_enabled: profile?.digest_enabled ?? true,
    digest_time:    profile?.digest_time    || '09:00',
  });
  const [saving,     setSaving]     = useState(false);
  const [soundOn,     setSoundOn]     = useState(() => isNotifSoundEnabled());
  const [uploading,  setUploading]  = useState(false);
  const [username,   setUsername]   = useState(profile?.username || '');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid' | 'yours'
  const [showUsernameSetup, setShowUsernameSetup] = useState(!profile?.username);
  const avatarRef = useRef(null);
  const usernameTimer = useRef(null);

  // On mount: if no username, suggest one
  useEffect(() => {
    if (!profile?.username && (profile?.full_name || user?.email)) {
      const suggested = generateUsername(profile?.full_name, user?.email);
      setUsername(suggested);
      checkUsername(suggested);
    } else if (profile?.username) {
      setUsername(profile.username);
    }
  }, [profile?.username]); // eslint-disable-line

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Debounced username availability check
  const checkUsername = useCallback(async (val) => {
    const clean = (val || '').toLowerCase().replace(/\s/g, '');
    const err = validateUsername(clean);
    if (err) { setUsernameStatus('invalid'); return; }
    if (clean === profile?.username) { setUsernameStatus('yours'); return; }
    setUsernameStatus('checking');
    clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(async () => {
      try {
        const { data: existing } = await db.checkUsername(clean);
        setUsernameStatus(existing ? 'taken' : 'available');
      } catch { setUsernameStatus(null); }
    }, 500);
  }, [profile?.username]);

  const handleUsernameChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setUsername(clean);
    checkUsername(clean);
  };

  const saveUsername = async () => {
    const clean = username.toLowerCase();
    const err = validateUsername(clean);
    if (err) { onToast('❌ ' + err); return; }
    if (usernameStatus === 'taken') { onToast('❌ Username already taken.'); return; }
    setUsernameSaving(true);
    try {
      await db.upsertProfile({ id: user.id, username: clean });
      refreshProfile();
      setShowUsernameSetup(false);
      setUsernameStatus('yours');
      onToast('✅ Username @' + clean + ' saved!');
    } catch(e) {
      onToast('❌ Could not save username: ' + (e.message || 'Unknown error'));
    }
    setUsernameSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    // Validate file
    if (!file.type.startsWith('image/')) { onToast('❌ Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { onToast('❌ Image must be under 5MB.'); return; }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const url  = await uploadFile(BUCKETS.avatars, path, file);
      await db.upsertProfile({ id: user.id, avatar_url: url });
      refreshProfile();
      onToast('✅ Avatar updated!');
    } catch(e) {
      const msg = e.message || '';
      if (msg.includes('Bucket not found') || msg.includes('avatars')) {
        onToast('❌ Avatar bucket not set up. Go to Supabase → Storage → Create bucket named "avatars" (public).');
      } else {
        onToast('❌ Upload failed: ' + msg);
      }
    }
    setUploading(false);
  };

  const saveProfile = async () => {
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      onToast('❌ Full name is required (min 2 characters).');
      return;
    }
    setSaving(true);
    try {
      await db.upsertProfile({ id: user.id, ...form, updated_at: new Date().toISOString() });
      refreshProfile();
      onToast('✅ Profile saved!');
    } catch(e) { onToast('❌ ' + e.message); }
    setSaving(false);
  };

  // Stats
  const totalGoals       = (data.goals || []).length;
  const completedHabits  = Object.values(data.habitLog || {}).filter(Boolean).length;
  const totalContent     = (data.contentPosts || []).length;
  const publishedContent = (data.contentPosts || []).filter(p => p.status === 'published').length;
  const booksRead        = (data.books || []).filter(b => b.status === 'read').length;
  const totalFiles       = (data.userFiles || []).length;
  const stats = [
    { label:'Goals',        value:totalGoals,       color:C.indigo  },
    { label:'Habits Logged',value:completedHabits,  color:C.emerald },
    { label:'Content Posts',value:totalContent,     color:'#8b5cf6' },
    { label:'Published',    value:publishedContent, color:C.amber   },
    { label:'Books Read',   value:booksRead,         color:C.sky     },
    { label:'Files',        value:totalFiles,        color:C.teal    },
  ];

  const usernameColor = {
    checking:  '#f59e0b',
    available: '#10b981',
    taken:     '#ef4444',
    invalid:   '#ef4444',
    yours:     '#6366f1',
  }[usernameStatus] || '#64748b';

  const usernameIcon = {
    checking:  '⏳',
    available: '✅',
    taken:     '❌',
    invalid:   '⚠️',
    yours:     '✓',
  }[usernameStatus] || '';

  return (
    <div style={{ maxWidth:800, margin:'0 auto', animation:'fadeUp .4s ease' }}>
      <div className="ta-eh-eyebrow">Account</div>
      <h1 className="ta-eh-title" style={{ fontSize:32, marginBottom:24 }}>My <em>profile</em>.</h1>

      {/* Username setup banner — shown until set */}
      {showUsernameSetup && (
        <div style={{ background:'linear-gradient(135deg,rgba(99,102,241,.1),rgba(139,92,246,.1))', border:'1px solid rgba(99,102,241,.3)', borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ fontSize:32 }}>🏷️</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:C.text, marginBottom:4 }}>Set your username</div>
            <div style={{ fontSize:12, color:C.text3, marginBottom:12 }}>
              Choose a unique username. You and others can use it to log in. Min 5 chars, lowercase letters, numbers, . _ - only.
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ position:'relative', flex:1 }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#6366f1', fontWeight:700, fontSize:14 }}>@</span>
                <input
                  value={username}
                  onChange={e => handleUsernameChange(e.target.value)}
                  placeholder="yourname123"
                  maxLength={30}
                  style={{ width:'100%', padding:'9px 12px 9px 28px', background:C.bg, border:`1.5px solid ${usernameColor}`, borderRadius:8, color:C.text, fontFamily:"'Inter',sans-serif", fontSize:14, outline:'none', boxSizing:'border-box' }}
                />
                {usernameStatus && (
                  <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:usernameColor }}>
                    {usernameIcon}
                  </span>
                )}
              </div>
              <Btn
                onClick={saveUsername}
                loading={usernameSaving}
                color={C.indigo}
                disabled={usernameStatus !== 'available' && usernameStatus !== 'yours'}
              >
                Save Username
              </Btn>
              {profile?.username && (
                <button onClick={() => setShowUsernameSetup(false)} style={{ background:'none', border:'none', color:C.text3, cursor:'pointer', fontSize:12 }}>✕</button>
              )}
            </div>
            {usernameStatus === 'available' && <div style={{ fontSize:11, color:'#10b981', marginTop:4 }}>✅ @{username} is available!</div>}
            {usernameStatus === 'taken' && <div style={{ fontSize:11, color:'#ef4444', marginTop:4 }}>❌ @{username} is taken. Try another.</div>}
            {usernameStatus === 'invalid' && <div style={{ fontSize:11, color:'#ef4444', marginTop:4 }}>⚠️ {validateUsername(username)}</div>}
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:20, marginBottom:20 }}>
        {/* Avatar + identity */}
        <Card title="Profile Photo" color={C.indigo} style={{ textAlign:'center' }}>
          <div style={{ position:'relative', display:'inline-block', marginBottom:16 }}>
            <Avatar src={profile?.avatar_url} name={profile?.full_name || profile?.email} size={96} style={{ margin:'0 auto' }}/>
            <button onClick={() => avatarRef.current?.click()} disabled={uploading} style={{
              position:'absolute', bottom:0, right:0, width:28, height:28,
              borderRadius:'50%', border:'2px solid #0f172a',
              background:'#6366f1', color:'#fff', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center',
            }}>{uploading ? '⏳' : '📷'}</button>
            <input ref={avatarRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarUpload}/>
          </div>
          <div style={{ fontWeight:700, color:C.text, marginBottom:2 }}>{profile?.full_name || 'User'}</div>
          {profile?.username && (
            <div style={{ fontSize:13, color:'#6366f1', fontWeight:600, marginBottom:4, cursor:'pointer' }}
              onClick={() => setShowUsernameSetup(true)}>
              @{profile.username}
            </div>
          )}
          <div style={{ fontSize:12, color:C.text3, marginBottom:8 }}>{profile?.email || user?.email}</div>
          <Badge color={C.indigo}>{profile?.role || 'user'}</Badge>
          {!profile?.username && (
            <button onClick={() => setShowUsernameSetup(true)} style={{ display:'block', margin:'10px auto 0', background:'none', border:'1px dashed #6366f1', color:'#6366f1', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:11 }}>
              + Set Username
            </button>
          )}
          <div style={{ marginTop:10, fontSize:11, color:C.text3 }}>
            Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US',{month:'long',year:'numeric'}) : 'N/A'}
          </div>
        </Card>

        {/* Edit form */}
        <Card title="Edit Profile" color={C.indigo}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="Full Name *" value={form.full_name} onChange={e=>upd('full_name',e.target.value)} placeholder="Your full name (required)"/>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:C.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'.05em' }}>Timezone</label>
              <select value={form.timezone} onChange={e=>upd('timezone',e.target.value)}
                style={{ width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontFamily:"'Inter',sans-serif", fontSize:13, outline:'none' }}>
                {['UTC','America/New_York','America/Los_Angeles','America/Chicago','Europe/London','Europe/Paris','Asia/Kolkata','Asia/Tokyo','Australia/Sydney'].map(tz=>(
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:C.bg, borderRadius:8, border:`1px solid ${C.border2}` }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>Push Notifications</div>
                <div style={{ fontSize:11, color:C.text3 }}>Get reminders and alerts</div>
              </div>
              <button onClick={()=>upd('notifications_enabled',!form.notifications_enabled)} style={{
                width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                background: form.notifications_enabled ? C.indigo : C.bg3,
                position:'relative', transition:'background .2s',
              }}>
                <div style={{ position:'absolute', top:2, left: form.notifications_enabled?20:2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s' }}/>
              </button>
            </div>

            {/* Notification Sound Toggle */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:C.bg, borderRadius:8, border:`1px solid ${C.border2}` }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>🔔 Notification Sound</div>
                <div style={{ fontSize:11, color:C.text3 }}>Play a tone for reminders, birthdays, tasks & requests</div>
              </div>
              <button onClick={()=>{ const next=!soundOn; setSoundOn(next); setNotifSoundEnabled(next); }} style={{
                width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                background: soundOn ? C.indigo : C.bg3,
                position:'relative', transition:'background .2s',
              }}>
                <div style={{ position:'absolute', top:2, left: soundOn?20:2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s' }}/>
              </button>
            </div>

            {/* Morning Digest Email */}
            <div style={{ background:C.bg, borderRadius:8, border:`1px solid ${C.border2}`, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>☀️ Morning Digest Email</div>
                  <div style={{ fontSize:11, color:C.text3 }}>Daily overview of tasks, goals & habits</div>
                </div>
                <button onClick={()=>upd('digest_enabled',!form.digest_enabled)} style={{
                  width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                  background: form.digest_enabled ? C.indigo : C.bg3,
                  position:'relative', transition:'background .2s', flexShrink:0,
                }}>
                  <div style={{ position:'absolute', top:2, left: form.digest_enabled?20:2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s' }}/>
                </button>
              </div>
              {form.digest_enabled && (
                <div style={{ padding:'0 14px 12px', borderTop:`1px solid ${C.border2}` }}>
                  <div style={{ fontSize:11, color:C.text3, margin:'10px 0 6px' }}>Send at (your local time)</div>
                  <input
                    type="time"
                    value={form.digest_time}
                    onChange={e=>upd('digest_time', e.target.value)}
                    style={{ padding:'6px 10px', borderRadius:6, border:`1px solid ${C.border2}`, background:C.bg2, color:C.text, fontSize:13, width:'100%' }}
                  />
                  <div style={{ fontSize:10, color:C.text3, marginTop:4 }}>
                    Default: 9:00 AM IST. Uses your timezone setting above.
                  </div>
                </div>
              )}
            </div>

            <Btn onClick={saveProfile} loading={saving} color={C.indigo}>💾 Save Changes</Btn>
          </div>
        </Card>
      </div>

      {/* Stats */}
      <Card title="Your Stats" color={C.violet} style={{ marginBottom:20 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
          {stats.map(s => (
            <div key={s.label} style={{ textAlign:'center', padding:14, background:C.bg, borderRadius:10, border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:26, fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Security */}
      <Card title="🔐 Account Security" color={C.rose}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:C.text, marginBottom:3 }}>Email address</div>
            <div style={{ fontSize:12, color:C.text3 }}>{user?.email}</div>
          </div>
          <Badge color={C.emerald}>Verified ✓</Badge>
        </div>
        {profile?.username && (
          <div style={{ borderTop:`1px solid ${C.border}`, marginTop:14, paddingTop:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:13, color:C.text, marginBottom:3 }}>Username</div>
              <div style={{ fontSize:12, color:'#6366f1', fontWeight:600 }}>@{profile.username}</div>
            </div>
            <button onClick={() => setShowUsernameSetup(true)} style={{ background:'none', border:`1px solid ${C.border2}`, color:C.text3, cursor:'pointer', borderRadius:6, padding:'5px 12px', fontSize:12 }}>Change</button>
          </div>
        )}
        <div style={{ borderTop:`1px solid ${C.border}`, marginTop:14, paddingTop:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:13, color:C.text, marginBottom:3 }}>Two identifiers for login</div>
            <div style={{ fontSize:11, color:C.text3 }}>Email + password · @username + password</div>
          </div>
        </div>
        <div style={{ borderTop:`1px solid ${C.border}`, marginTop:14, paddingTop:14 }}>
          <MFASetup/>
        </div>
      </Card>
    </div>
  );
}
